import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for managing ProjectTemplateCard state
 */
export const useProjectTemplateState = ({
  selectedProjectTemplate: initialSelectedTemplate,
  projectTemplateGroup,
  animateExpand,
}) => {
  const [showVersions, setShowVersions] = useState(false);
  const [selectedProjectTemplate, setSelectedProjectTemplate] = useState(null);
  const [hoveredOver, setHoveredOver] = useState(false);
  const [pencilHoveredOver, setPencilHoveredOver] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userHasToggled, setUserHasToggled] = useState(false);
  const [lastSelectedTemplateId, setLastSelectedTemplateId] = useState(null);

  // Update selected template when props change
  const updateSelectedTemplate = useCallback((newSelectedTemplate) => {
    const newTemplateId = newSelectedTemplate ? newSelectedTemplate.id : null;
    
    // Only update if the template ID has actually changed
    if (newTemplateId !== lastSelectedTemplateId) {
      setLastSelectedTemplateId(newTemplateId);
      
      let newShowVersions = showVersions;

      if (newSelectedTemplate) {
        if (projectTemplateGroup.find(template => template.id === newSelectedTemplate.id)) {
          // Only auto-expand if user hasn't manually toggled
          if (!userHasToggled) {
            newShowVersions = true;
          }
        } else {
          newSelectedTemplate = null;
        }
      }
      
      setShowVersions(newShowVersions);
      setSelectedProjectTemplate(newSelectedTemplate);
    }
  }, [showVersions, projectTemplateGroup, userHasToggled, lastSelectedTemplateId]);

  // Initialize selected template
  useEffect(() => {
    updateSelectedTemplate(initialSelectedTemplate);
  }, [initialSelectedTemplate, updateSelectedTemplate]);

  // Get most recent template version
  const getMostRecentTemplateVersion = useCallback(() => {
    let mostRecent = null;
    projectTemplateGroup.forEach(template => {
      mostRecent = template;
    });
    return mostRecent;
  }, [projectTemplateGroup]);

  // Toggle versions display
  const toggleVersions = useCallback(() => {
    setShowVersions(prev => !prev);
    setUserHasToggled(true);
  }, []);

  // Handle card click
  const handleCardClick = useCallback(() => {
    setHoveredOver(true);
    toggleVersions();
  }, [toggleVersions]);

  // Handle hover events
  const handleHoverIn = useCallback((currentUser) => {
    const mostRecentTemplate = getMostRecentTemplateVersion();
    
    if (!menuOpen &&
      (((currentUser && currentUser.role && currentUser.role.name === 'admin') || (currentUser && currentUser.super_admin)) && mostRecentTemplate) &&
      showVersions && !hoveredOver) {
      setHoveredOver(true);
    }
  }, [menuOpen, getMostRecentTemplateVersion, showVersions, hoveredOver]);

  const handleHoverOut = useCallback(() => {
    setHoveredOver(false);
  }, []);

  const handlePencilHoverIn = useCallback(() => {
    setPencilHoveredOver(true);
  }, []);

  const handlePencilHoverOut = useCallback(() => {
    setPencilHoveredOver(false);
  }, []);

  // Handle menu operations
  const handlePencilClick = useCallback((e) => {
    e.stopPropagation();
    setMenuOpen(true);
    setHoveredOver(false);
    setPencilHoveredOver(false);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuOpen(false);
    setHoveredOver(false);
    setPencilHoveredOver(false);
  }, []);

  return {
    // State
    showVersions,
    selectedProjectTemplate,
    hoveredOver,
    pencilHoveredOver,
    menuOpen,
    
    // Actions
    updateSelectedTemplate,
    getMostRecentTemplateVersion,
    toggleVersions,
    handleCardClick,
    handleHoverIn,
    handleHoverOut,
    handlePencilHoverIn,
    handlePencilHoverOut,
    handlePencilClick,
    handleCloseMenu,
  };
};



// WEBPACK FOOTER //
// ./src/containers/ProjectTypesContainer/ProjectTemplateCard/hooks/useProjectTemplateState.js